'use client';

import * as React from 'react';
import { Bell, BellOff } from 'lucide-react';

import type { Banner } from '@nutralab/api';
import { Heading, Icon, IconWithBadge, Popover, Text } from '@nutralab/ui';

import { BannerCard } from './_components/BannerCard';
import styles from './BannerNotifications.module.scss';

import { AppLayer } from '@/shared/constants';

interface BannerNotificationsProps {
    label: string;
    title: string;
    emptyMessage: string;
    banners: Banner[];
}

/** The navbar bell: CMS banners shown as notifications in a popover. */
export function BannerNotifications({
    label,
    title,
    emptyMessage,
    banners,
}: BannerNotificationsProps): React.ReactElement {
    const [open, setOpen] = React.useState(false);

    const handleNavigate = (): void => {
        setOpen(false);
    };

    return (
        <Popover
            zIndex={AppLayer.POPOVER}
            open={open}
            onOpenChange={setOpen}
            className={styles.content}
            trigger={<IconWithBadge icon={<Bell />} label={label} count={banners.length} />}
        >
            <Heading size="lg" weight="medium" className={styles.title}>
                {title}
            </Heading>

            {banners.length === 0 ? (
                <div className={styles.empty}>
                    <Icon icon={BellOff} size="4xl" color="muted" />
                    <Text color="muted" align="center">
                        {emptyMessage}
                    </Text>
                </div>
            ) : (
                <div className={styles.list}>
                    {banners.map((banner) => (
                        <BannerCard
                            key={`${banner.link}-${banner.title}`}
                            banner={banner}
                            onNavigate={handleNavigate}
                        />
                    ))}
                </div>
            )}
        </Popover>
    );
}
